import axios from "axios";
import { getSessionId } from "@/lib/session";
import { getToken } from "@/lib/authToken";
import { logError } from "@/lib/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API });

// Attach the per-tab session id (ephemeral CRM workspace) AND, when present, the
// auth JWT (identifies the logged-in user) to every request.
client.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-Session-Id"] = getSessionId();
  const token = getToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// Best-effort server-side cleanup when the user closes/leaves the tab. Uses
// sendBeacon (which cannot set headers), so the session id goes as a query param.
export function beaconCleanup() {
  try {
    const sid = getSessionId();
    if (!sid) return;
    const url = `${API}/session/cleanup?sid=${encodeURIComponent(sid)}`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url);
    } else {
      // Fallback for browsers without sendBeacon
      fetch(url, { method: "POST", keepalive: true }).catch(() => {});
    }
  } catch (error) {
    logError("Session cleanup beacon failed:", error);
  }
}

export const api = {
  status: () => client.get("/hubspot/status").then((r) => r.data),
  connect: (credential, save = false) => client.post("/hubspot/connect", { credential, save }).then((r) => r.data),
  connectSaved: () => client.post("/hubspot/connect-saved").then((r) => r.data),
  getSavedKey: () => client.get("/saved-key").then((r) => r.data),
  forgetSavedKey: () => client.delete("/saved-key").then((r) => r.data),
  sync: () => client.post("/hubspot/sync").then((r) => r.data),
  syncStatus: () => client.get("/hubspot/sync/status").then((r) => r.data),
  disconnect: () => client.post("/hubspot/disconnect").then((r) => r.data),
  uploadCsv: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return client
      .post("/upload/csv", fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data);
  },
  analytics: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/analytics", { params }).then((r) => r.data);
  },
  analyticsOverview: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/analytics/overview", { params }).then((r) => r.data);
  },
  generateReport: () => client.post("/analyst/report").then((r) => r.data),
  reportStatus: () => client.get("/analyst/report/status").then((r) => r.data),
  getReport: () => client.get("/analyst/report").then((r) => r.data),
  saveReport: (report_md) =>
    client.put("/analyst/report", { report_md }).then((r) => r.data),
  clearReport: () => client.delete("/analyst/report").then((r) => r.data),
  // Analyst chat (grounded Q&A)
  chat: (message) => client.post("/chat", { message }).then((r) => r.data),
  getChat: () => client.get("/chat").then((r) => r.data),
  setChatIncluded: (id, included) =>
    client.patch(`/chat/${id}/include`, { included }).then((r) => r.data),
  deleteChat: (id) => client.delete(`/chat/${id}`).then((r) => r.data),
  // Connections (contacts + relationship mapping)
  getContacts: () => client.get("/contacts").then((r) => r.data),
  syncContacts: (limit) => client.post("/contacts/sync", { limit }).then((r) => r.data),
  getConnections: () => client.get("/connections").then((r) => r.data),
  generateConnectionsReport: () => client.post("/connections/report").then((r) => r.data),
  connectionsReportStatus: () => client.get("/connections/report/status").then((r) => r.data),
  getConnectionsReport: () => client.get("/connections/report").then((r) => r.data),
  clearConnections: () => client.delete("/connections").then((r) => r.data),
  // Reps / Team
  getTeam: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/team", { params }).then((r) => r.data);
  },
  generateTeamReport: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.post("/team/report", null, { params }).then((r) => r.data);
  },
  teamReportStatus: () => client.get("/team/report/status").then((r) => r.data),
  getTeamReport: () => client.get("/team/report").then((r) => r.data),
  // Activity Insights
  getActivityInsights: (start, end, granularity) => {
    const params = { granularity: granularity || "monthly" };
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/activity-insights", { params }).then((r) => r.data);
  },
  generateActivityReport: (start, end, granularity) => {
    const params = { granularity: granularity || "monthly" };
    if (start) params.start = start;
    if (end) params.end = end;
    return client.post("/activity-insights/report", null, { params }).then((r) => r.data);
  },
  activityReportStatus: () => client.get("/activity-insights/report/status").then((r) => r.data),
  getActivityReport: () => client.get("/activity-insights/report").then((r) => r.data),
  // Forecast
  getForecast: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/forecast", { params }).then((r) => r.data);
  },
  generateForecastReport: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.post("/forecast/report", null, { params }).then((r) => r.data);
  },
  forecastReportStatus: () => client.get("/forecast/report/status").then((r) => r.data),
  getForecastReport: () => client.get("/forecast/report").then((r) => r.data),
  // Pipeline / Funnel
  getFunnel: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.get("/funnel", { params }).then((r) => r.data);
  },
  generateFunnelReport: (start, end) => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    return client.post("/funnel/report", null, { params }).then((r) => r.data);
  },
  funnelReportStatus: () => client.get("/funnel/report/status").then((r) => r.data),
  getFunnelReport: () => client.get("/funnel/report").then((r) => r.data),
  // Deals (master list)
  getDeals: () => client.get("/deals").then((r) => r.data),
  generateDealsReport: () => client.post("/deals/report").then((r) => r.data),
  dealsReportStatus: () => client.get("/deals/report/status").then((r) => r.data),
  getDealsReport: () => client.get("/deals/report").then((r) => r.data),
  // Data Health
  getDataHealth: () => client.get("/data-health").then((r) => r.data),
  generateDataHealthReport: () => client.post("/data-health/report").then((r) => r.data),
  dataHealthReportStatus: () => client.get("/data-health/report/status").then((r) => r.data),
  getDataHealthReport: () => client.get("/data-health/report").then((r) => r.data),
  // CRM Inventory (scope-aware object counts)
  getCrmInventory: () => client.get("/crm-inventory").then((r) => r.data),
  // API usage (HubSpot API-call breakdown)
  getApiUsage: () => client.get("/api-usage").then((r) => r.data),
  // Auth
  register: (email, password) => client.post("/auth/register", { email, password }).then((r) => r.data),
  login: (email, password) => client.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => client.get("/auth/me").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    client.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  // Admin
  adminUsers: () => client.get("/admin/users").then((r) => r.data),
  adminEvents: () => client.get("/admin/events").then((r) => r.data),
  adminStats: () => client.get("/admin/stats").then((r) => r.data),
  adminStatsDrilldown: (metric) =>
    client.get("/admin/stats/drilldown", { params: { metric } }).then((r) => r.data),
  adminSetStatus: (id, status) => client.patch(`/admin/users/${id}`, { status }).then((r) => r.data),
  adminResetPassword: (id, new_password) =>
    client.post(`/admin/users/${id}/reset-password`, { new_password }).then((r) => r.data),
  adminResetPasswordByEmail: (email, new_password) =>
    client.post(`/admin/users/reset-password-by-email`, { email, new_password }).then((r) => r.data),
  adminDeleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  // Support — user-facing
  createSupportTicket: (payload) => client.post("/support/tickets", payload).then((r) => r.data),
  createPublicSupportTicket: (payload) => client.post("/support/public", payload).then((r) => r.data),
  mySupportTickets: () => client.get("/support/my").then((r) => r.data),
  userReplySupportTicket: (id, body) =>
    client.post(`/support/tickets/${id}/reply`, { body }).then((r) => r.data),
  // Support — admin
  adminSupportTickets: () => client.get("/admin/support/tickets").then((r) => r.data),
  adminSupportTicket: (id) => client.get(`/admin/support/tickets/${id}`).then((r) => r.data),
  adminSupportSetRead: (id, read) =>
    client.post(`/admin/support/tickets/${id}/read`, { read }).then((r) => r.data),
  adminSupportSetStar: (id, starred) =>
    client.post(`/admin/support/tickets/${id}/star`, { starred }).then((r) => r.data),
  adminSupportSetArchive: (id, archived) =>
    client.post(`/admin/support/tickets/${id}/archive`, { archived }).then((r) => r.data),
  adminSupportReply: (id, body) =>
    client.post(`/admin/support/tickets/${id}/reply`, { body }).then((r) => r.data),
  adminSupportForward: (id, to, body) =>
    client.post(`/admin/support/tickets/${id}/forward`, { to, body }).then((r) => r.data),
  adminSupportDelete: (id) => client.delete(`/admin/support/tickets/${id}`).then((r) => r.data),
  // Saved prompts (Query Data)
  listSavedPrompts: () => client.get("/saved-prompts").then((r) => r.data),
  createSavedPrompt: (text, label) =>
    client.post("/saved-prompts", { text, label }).then((r) => r.data),
  updateSavedPrompt: (id, payload) =>
    client.put(`/saved-prompts/${id}`, payload).then((r) => r.data),
  deleteSavedPrompt: (id) => client.delete(`/saved-prompts/${id}`).then((r) => r.data),
};

// Start the background HubSpot sync and poll until done. Resolves with the final
// {counts, api_usage, warnings, ...} result, or throws with a helpful message.
// Polls every 3s up to ~10 minutes (long syncs run server-side without a
// gateway timeout because the request returns immediately).
// `shouldCancel` (optional) is checked each tick; if it returns true, polling
// stops and a CancelledError is thrown (the server sync keeps running harmlessly
// and its result is simply ignored by this client).
export class SyncCancelledError extends Error {
  constructor() {
    super("Sync cancelled.");
    this.name = "SyncCancelledError";
    this.cancelled = true;
  }
}

export async function runSync(onTick, shouldCancel) {
  const cancelled = () => (typeof shouldCancel === "function" ? !!shouldCancel() : false);
  if (cancelled()) throw new SyncCancelledError();
  await api.sync(); // => {status:'running'}
  const started = Date.now();
  const MAX_MS = 10 * 60 * 1000;
  // small helper delay
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  // give the job a moment before first poll
  await wait(1500);
  if (cancelled()) throw new SyncCancelledError();
  while (Date.now() - started < MAX_MS) {
    let st;
    try {
      st = await api.syncStatus();
    } catch (e) {
      st = null;
    }
    if (st) {
      if (onTick) onTick(st);
      if (st.status === "done") return st;
      if (st.status === "error") {
        const err = new Error(st.error || "Sync failed.");
        err.detail = st.error;
        throw err;
      }
    }
    if (cancelled()) throw new SyncCancelledError();
    await wait(3000);
  }
  throw new Error("Sync is taking longer than expected. Please try again.");
}

export const fmtCurrency = (n) => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
};

export const fmtNumber = (n) => {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("en-US").format(n);
};
